import { useUser } from 'contexts/UserContext';
import { format } from 'date-fns';
import { useCallback } from 'react';

/**
 * The default date format.
 */
export const defaultDateFormat = 'dd-MMM-yyyy';

/**
 * The default time format.
 */
export const defaultTimeFormat = 'hh:mm:ss aa';

/**
 * Datetime format for displaying an unambiguous moment in time.
 */
export const canonicalDateTimeFormat = 'yyyy-MM-dd HH:mm:ss.SSSxxx';

/*
Current API Formats returned (as of 2020-11-10):
DateFormat:
    * `dd-MM-yyyy`  === Label "DD-MM-YYYY"      => `10-11-2020` (November 10, 2020)
    * `dd-MMM-yyyy` === Label "DD-Month-YYYY"   => `02-Nov-2020` (November 2, 2020)
    * `MM-dd-yyyy`  === Label "MM-DD-YYYY"      => `11-02-2020` (November 2, 2020)
    * `yyyy-MM-dd`  === Label "YYYY-MM-DD"      => `2020-11-02` (November 2, 2020)
TimeFormat:
    * `hh:mm:ss tt` === Label "AM/PM Format"    => `2:06:49 PM`
    * `HH:mm:ss`    === Label "24 Hour Format"  => `14:06:49` (2:06:49 PM)
*/
/**
 * Converts a format coming from the API to a format that can be parsed by
 * the `date-fns` `format()` function.
 * @param apiFormat The format to convert.
 */
const convertApiFormatToDateFnsFormat = (apiFormat: string, showSeconds?: boolean) => {
    switch (apiFormat) {
        case 'hh:mm:ss tt':
            return showSeconds ? 'hh:mm:ss aa' : 'hh:mm aa';
        default:
            return apiFormat;
    }
};
/**
 * Converts a format coming from the `useUserDateTimeFormat` hook, or a datetime
 * format already converted by `convertApiFormatToDateFnsFormat` to a format that
 * can be used by Kendo.
 * @param apiFormat The format to convert.
 * @see https://github.com/telerik/kendo-intl/blob/master/docs/date-formatting/index.md
 */
const convertApiFormatToKendoFormat = (apiFormat: string) => {
    switch (apiFormat) {
        case 'hh:mm:ss tt':
            return 'hh:mm:ss aa';
        default:
            return apiFormat;
    }
};

/**
 * Returns functions used to format dates and times according to a user's preferences.
 *
 * @param showSeconds Whether or not to show seconds in the time. Defaults to `true`
 */
export function useUserDateTimeFormat(showSeconds = true) {
    const user = useUser();

    const dateFormat = convertApiFormatToDateFnsFormat(
        user.user?.Profile.DateFormat ?? defaultDateFormat,
    );
    const timeFormat = convertApiFormatToDateFnsFormat(
        user.user?.Profile.TimeFormat ?? defaultTimeFormat,
        showSeconds,
    );
    const dateTimeFormat = `${dateFormat} ${timeFormat}`;

    const kendoDateFormat = convertApiFormatToKendoFormat(
        user.user?.Profile.DateFormat ?? defaultDateFormat,
    );
    const kendoTimeFormat = convertApiFormatToKendoFormat(
        user.user?.Profile.TimeFormat ?? defaultTimeFormat,
    );
    const kendoDateTimeFormat = `${kendoDateFormat} ${kendoTimeFormat}`;

    // TODO: We should be handling time zones here, but currently the
    // API does not return canonical IANA time zones (e.g., `America/New_York`) but
    // instead returns a time zone like "Eastern Standard Time", which is not trivial
    // to parse. Once the API returns an IANA time zone, we can handle that here.
    const formatDate = useCallback(
        (date: Date) => format(date, dateFormat),
        [dateFormat],
    );
    const formatTime = useCallback(
        (date: Date) => format(date, timeFormat),
        [timeFormat],
    );
    const formatDateTime = useCallback(
        (date: Date) => format(date, dateTimeFormat),
        [dateTimeFormat],
    );

    return {
        /**
         * Format a date.
         */
        formatDate,
        /**
         * Format a time.
         */
        formatTime,
        /**
         * Format a date and time.
         */
        formatDateTime,
        /**
         * The date format used.
         */
        dateFormat,
        /**
         * The time format used.
         */
        timeFormat,
        /**
         * The datetime format used.
         */
        dateTimeFormat,
        /**
         * The time format that can be used with Kendo components.
         */
        kendoTimeFormat,
        /**
         * The date format that can be used with Kendo components.
         */
        kendoDateFormat,
        /**
         * The datetime format that can be used with Kendo components.
         */
        kendoDateTimeFormat,
    };
}
